import React, { useState } from 'react';
import { PenTool, Download, FileText } from 'lucide-react';
import { Button, Card, FileUploadZone, ProgressBar, Alert, Badge, PDFPreview, ToolActionButtons, NoteCard } from '../shared';
import { PDFDocument, rgb, StandardFonts } from 'pdf-lib';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import '../../styles/design-system.css';

const SignPDF = () => {
    const [file, setFile] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [pageCount, setPageCount] = useState(0);
    const [signatureText, setSignatureText] = useState('');
    const [signatureType, setSignatureType] = useState('text');
    const [position, setPosition] = useState('bottom-right');
    const [previewBlob, setPreviewBlob] = useState(null);
    const [previewFilename, setPreviewFilename] = useState('');

    const handleFileSelected = async (files) => {
        if (files && files.length > 0) {
            const selectedFile = files[0];
            setFile(selectedFile);

            try {
                const arrayBuffer = await selectedFile.arrayBuffer();
                const pdf = await PDFDocument.load(arrayBuffer);
                setPageCount(pdf.getPageCount());
                toast.success(`PDF loaded: ${pdf.getPageCount()} pages`);
            } catch (error) {
                toast.error(`Failed to load PDF: ${error.message}`);
            }
        }
    };

    const signPDF = async () => {
        if (!file || !signatureText) {
            toast.warning('Please enter signature text');
            return;
        }

        try {
            setIsProcessing(true);
            setProgress(10);

            const arrayBuffer = await file.arrayBuffer();
            const pdfDoc = await PDFDocument.load(arrayBuffer);
            const font = await pdfDoc.embedFont(StandardFonts.HelveticaBoldOblique);

            setProgress(30);

            const pages = pdfDoc.getPages();
            const lastPage = pages[pages.length - 1];
            const { width, height } = lastPage.getSize();

            const fontSize = 14;
            const text = signatureText;
            const textWidth = font.widthOfTextAtSize(text, fontSize);

            const timestamp = new Date().toLocaleString();
            const timestampWidth = font.widthOfTextAtSize(timestamp, 10);

            let x, y;
            switch (position) {
                case 'bottom-left':
                    x = 50;
                    y = 50;
                    break;
                case 'bottom-right':
                    x = width - Math.max(textWidth, timestampWidth) - 50;
                    y = 50;
                    break;
                case 'top-right':
                    x = width - Math.max(textWidth, timestampWidth) - 50;
                    y = height - 100;
                    break;
                case 'top-left':
                    x = 50;
                    y = height - 100;
                    break;
                default:
                    x = width - Math.max(textWidth, timestampWidth) - 50;
                    y = 50;
            }

            // Draw signature
            lastPage.drawText(text, {
                x,
                y: y + 20,
                size: fontSize,
                font,
                color: rgb(0, 0, 0.8),
            });

            // Draw timestamp
            lastPage.drawText(timestamp, {
                x,
                y,
                size: 10,
                font,
                color: rgb(0.3, 0.3, 0.3),
            });

            // Draw line above signature
            lastPage.drawLine({
                start: { x, y: y + 18 },
                end: { x: x + textWidth, y: y + 18 },
                thickness: 1,
                color: rgb(0, 0, 0),
            });

            setProgress(90);

            const pdfBytes = await pdfDoc.save();
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });

            setProgress(100);

            const fileName = file.name.replace(/\.pdf$/i, '');
            const filename = `${fileName}_signed.pdf`;

            // Set preview
            setPreviewBlob(blob);
            setPreviewFilename(filename);

            toast.success('PDF signed! Opening preview...');

            setTimeout(() => setProgress(0), 1000);

        } catch (error) {
            console.error('Error:', error);
            toast.error(`Failed to sign PDF: ${error.message}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div>
            {/* Action Buttons */}
            <ToolActionButtons
                onAdd={() => {
                    document.querySelector('input[type="file"]')?.click();
                }}
                notificationCount={file ? 1 : 0}
            />

            <div className="tool-header">
                <h1 className="tool-title">
                    <PenTool size={36} style={{ display: 'inline', marginRight: 'var(--spacing-3)', color: 'var(--color-accent)' }} />
                    Sign PDF
                </h1>
                <p className="tool-description">
                    Add your digital signature to PDF documents with timestamp.
                </p>
            </div>

            {!file ? (
                <FileUploadZone
                    onFilesSelected={handleFileSelected}
                    acceptedTypes={['application/pdf']}
                    multiple={false}
                    title="Drop PDF file here"
                    description="or click to browse"
                />
            ) : (
                <Alert variant="success" title="PDF Loaded">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                            <FileText size={16} />
                            <span>{file.name}</span>
                            {pageCount > 0 && <Badge variant="info">{pageCount} pages</Badge>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => { setFile(null); setPageCount(0); }}>
                            Change PDF
                        </Button>
                    </div>
                </Alert>
            )}

            {file && (
                <>
                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Card>
                            <h3 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-bold)', marginBottom: 'var(--spacing-4)' }}>
                                Signature Settings
                            </h3>

                            <div style={{ marginBottom: 'var(--spacing-4)' }}>
                                <label className="label">Signature Text</label>
                                <input
                                    type="text"
                                    value={signatureText}
                                    onChange={(e) => setSignatureText(e.target.value)}
                                    className="input"
                                    placeholder="Enter your name or signature"
                                />
                            </div>

                            <div>
                                <label className="label">Position on Last Page</label>
                                <select value={position} onChange={(e) => setPosition(e.target.value)} className="input">
                                    <option value="bottom-right">Bottom Right</option>
                                    <option value="bottom-left">Bottom Left</option>
                                    <option value="top-right">Top Right</option>
                                    <option value="top-left">Top Left</option>
                                </select>
                            </div>
                        </Card>
                    </div>

                    {isProcessing && (
                        <div style={{ marginTop: 'var(--spacing-6)' }}>
                            <ProgressBar value={progress} striped showLabel />
                        </div>
                    )}

                    <div style={{ marginTop: 'var(--spacing-6)' }}>
                        <Button variant="primary" size="lg" onClick={signPDF} disabled={isProcessing || !signatureText} loading={isProcessing} fullWidth>
                            <PenTool size={20} />
                            Sign PDF
                        </Button>
                    </div>

                    <NoteCard variant="info">
                        <strong>Note:</strong> Signature will be added to the last page with a timestamp.
                    </NoteCard>
                </>
            )}

            {/* PDF Preview Modal */}
            {previewBlob && (
                <PDFPreview
                    pdfBlob={previewBlob}
                    filename={previewFilename}
                    onClose={() => setPreviewBlob(null)}
                    onDownload={() => {
                        if (previewBlob && previewFilename) {
                            saveAs(previewBlob, previewFilename);
                            toast.success('Download started!');
                            setPreviewBlob(null);
                        }
                    }}
                />
            )}
        </div>
    );
};

export default SignPDF;
